import { Component, Input, OnDestroy, OnInit, Type, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { HttpHelpService } from "../service/http-help.service";
import { NgxSpinnerService } from "ngx-spinner";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { MessageService } from 'primeng/api';
import { FilterService } from 'primeng/api';
import Swal from 'sweetalert2';
import { AddMerchantUserEndPoient, AddUserEndPoient } from "../service/global.service";
import { israelPhoneNumberValidator } from './../../validators/israelPhoneNumberValidator';
import { passwordValidators } from "../../validators/passwordValidators";
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

/**
 * Component for managing users, including listing, adding, and modifying user status.
 * It handles different user types (standard and merchant) and integrates with
 * services for data fetching, translation, modal display, notifications, and loading spinners.
 */
@Component({
	selector: "app-users",
	templateUrl: "./users.component.html",
	styleUrls: ["./users.component.scss"],
	providers: [MessageService]
})
export class UsersComponent implements OnInit, OnDestroy {
	/** Stores the current language setting retrieved from local storage. */
	curentLang: string = "";
	/** Stores the current layout version (e.g., 'light-only', 'dark-only') from local storage. */
	layoutVersion: any;
	/** The ID of the user performing the registration or modification actions, retrieved from local storage. */
	userRegistId: any = JSON.parse(localStorage.getItem('user_id'));
	/** The ID of the merchant associated with the current session, retrieved from local storage. */
	merchantId = JSON.parse(localStorage.getItem('merchant_id'));
	/** The type of the currently logged-in user, retrieved from local storage. */
	user_type = JSON.parse(localStorage.getItem('user_type'));
	/** Search term for filtering (potentially deprecated in favor of globalFilterValue). */
	term: string = '';
	/** Placeholder for pagination related data, likely used with ngx-pagination or similar. */
	p: any;
	/** FormGroup instance for the 'add standard user' form. */
	addUser: FormGroup;
	/** FormGroup instance for the 'add merchant user' form. */
	merchantUser: FormGroup;
	/** Array to hold data related to merchant customers. Usage context might be specific. */
	customerDataMerchant: any = [];
	/** Holds the ID of a receipt, potentially for linking or reference. Usage context might be specific. */
	receiptId: number;
	/** Placeholder for data intended for update operations. Usage context might be specific. */
	updatedData: any;
	/** Placeholder for data intended for display or view operations. Usage context might be specific. */
	viewData: any;
	/** Placeholder for a user's phone number. Usage context might be specific. */
	UserPhone: any;
	/** Array to store the list of users fetched from the backend. */
	userData: any = [];
	/** Array to store the list of available user types fetched from the backend. */
	userTypeData: any = [];
	/** Boolean flag to control the visibility of password fields. */
	public show: boolean = false;
	/** Stores the ID of the user currently selected or being acted upon. */
	userId: any;
	/** Input property to receive a user ID, potentially when this component is used within a modal. */
	@Input() userModalId: number;
	/** Boolean flag used to trigger validation states or messages in the template. */
	public validate = false;
	/** Provides a type reference to the UsersComponent itself. */
	usersComponent: Type<UsersComponent> = UsersComponent;
	/** Input property indicating whether the component is rendered inside a modal dialog. */
	@Input() isInsideModal: boolean = false;
	/** Boolean flag potentially related to profile display variations. */
	public isProfile2 = true;
	/** Tracks the index of the currently expanded collapsible section, or null if none are open. */
	openedIndex: number | null = null;
	/** Stores the value entered into the global filter input for the PrimeNG table. */
	globalFilterValue: string = '';
	/** Scope selector in users header: all users or admin users only. */
	userScope: 'all' | 'admin' = 'all';
	/** Cached users from current token endpoint. */
	private allUsersData: any[] = [];
	/** Cached users from platform admin endpoint. */
	private platformUsersData: any[] = [];
	/** Total platform users returned by the admin endpoint. */
	platformUsersTotal = 0;
	/** Current platform users page (1-based). */
	platformPage = 1;
	/** Page size for platform users list. */
	platformRows = 10;
	/** Loading state for platform users table/cards. */
	platformLoading = false;
	/** Current my-users page (1-based). */
	myUsersPage = 1;
	/** Page size for my-users list. */
	myUsersRows = 10;
	/** Loading state for my-users table/cards. */
	myUsersLoading = false;
	private platformSearchSubject = new Subject<string>();
	private myUsersSearchSubject = new Subject<string>();
	private destroy$ = new Subject<void>();
	@ViewChild('dt') usersTable?: Table;
	/** Only admin should use platform users endpoint. */
	get canUsePlatformUsers(): boolean {
		return +this.user_type === 200;
	}

	/**
	 * Initializes the component, injects dependencies, retrieves initial data from local storage,
	 * and sets up the reactive forms for adding users.
	 * @param spinner - Service for showing/hiding loading spinners.
	 * @param translate - Service for handling internationalization.
	 * @param service - Service for making HTTP requests.
	 * @param modalService - Service for opening and managing modal dialogs.
	 * @param messageService - Service for displaying toast messages.
	 */
	constructor(
		private spinner: NgxSpinnerService,
		public translate: TranslateService,
		private service: HttpHelpService,
		private modalService: NgbModal,
		private messageService: MessageService
	) {

		this.curentLang = localStorage.getItem('curentLang') || 'en';
		this.merchantId = JSON.parse(localStorage.getItem('merchant_id') || 'null');
		this.user_type = JSON.parse(localStorage.getItem('user_type') || 'null');
		this.userRegistId = JSON.parse(localStorage.getItem('user_id') || 'null');

		this.addUser = new FormGroup({
			'phone_number': new FormControl('', [Validators.required, israelPhoneNumberValidator()]),
			user_name: new FormControl('', [Validators.required, Validators.maxLength(20)]),
			language: new FormControl(this.curentLang),
			password: new FormControl('', passwordValidators),
			user_type: new FormControl(''),
			created_by: new FormControl(this.userRegistId),
		});

		this.merchantUser = new FormGroup({
			'phone_number': new FormControl('', [Validators.required, israelPhoneNumberValidator()]),
			user_name: new FormControl('', [Validators.required, Validators.maxLength(20)]),
			password: new FormControl('', passwordValidators),
			merchant_id: new FormControl(this.merchantId),
			created_by: new FormControl(this.userRegistId),
		});
	}

	/**
	 * Toggles the collapse state (expanded/collapsed) of a specific section identified by its index.
	 * If the clicked section is already open, it closes it. Otherwise, it opens the clicked section.
	 * @param index - The index of the section to toggle.
	 */
	toggleCollapse(index: number): void {
		this.openedIndex = this.openedIndex === index ? null : index;
	}

	/**
	 * Returns userData filtered by globalFilterValue for the mobile/tablet card view.
	 * Matches user_name and phone_number (contains, case-insensitive).
	 */
	getFilteredUsersForCards(): any[] {
		if (!this.userData || !this.userData.length) return [];
		return this.userData;
	}

	get usersTotalCount(): number {
		if (this.isPlatformUsersMode()) {
			return this.platformUsersTotal;
		}
		return this.allUsersTotal;
	}

	get usersTotalCountLabel(): string {
		const count = this.usersTotalCount;
		const key = this.isPlatformUsersMode()
			? 'users.info.totalPlatformUsers'
			: 'users.info.totalUsers';
		return this.translate.instant(key, { count });
	}

	get myUsersPageReport(): string {
		if (!this.allUsersTotal) {
			return this.curentLang === 'ar'
				? 'لا يوجد مستخدمين'
				: this.curentLang === 'he'
					? 'אין משתמשים'
					: 'No users';
		}
		const first = (this.myUsersPage - 1) * this.myUsersRows + 1;
		const last = Math.min(this.myUsersPage * this.myUsersRows, this.allUsersTotal);
		if (this.curentLang === 'ar') {
			return `عرض ${first} إلى ${last} من ${this.allUsersTotal}`;
		}
		if (this.curentLang === 'he') {
			return `מציג ${first} עד ${last} מתוך ${this.allUsersTotal}`;
		}
		return `Showing ${first} to ${last} of ${this.allUsersTotal}`;
	}

	get platformPageReport(): string {
		if (!this.platformUsersTotal) {
			return this.curentLang === 'ar'
				? 'لا يوجد مستخدمين'
				: this.curentLang === 'he'
					? 'אין משתמשים'
					: 'No users';
		}
		const first = (this.platformPage - 1) * this.platformRows + 1;
		const last = Math.min(this.platformPage * this.platformRows, this.platformUsersTotal);
		if (this.curentLang === 'ar') {
			return `عرض ${first} إلى ${last} من ${this.platformUsersTotal}`;
		}
		if (this.curentLang === 'he') {
			return `מציג ${first} עד ${last} מתוך ${this.platformUsersTotal}`;
		}
		return `Showing ${first} to ${last} of ${this.platformUsersTotal}`;
	}

	/**
	 * Formats a given phone number string. It removes all non-digit characters except for spaces
	 * and ensures the resulting string ends with a '+' sign.
	 * Note: The logic to ensure it ends with '+' might be specific and potentially incorrect for standard formatting.
	 * @param phoneNumber - The raw phone number string to format.
	 * @returns The formatted phone number string.
	 */
	formatPhoneNumber(phoneNumber: string): string {

		const cleaned = phoneNumber.replace(/[^ \d]/g, '');

		return cleaned.endsWith('+') ? cleaned : `${cleaned}+`;
	}

	/**
	 * Fetches all users associated with the currently authenticated user's token
	 * using the `HttpHelpService`. Displays a spinner during the request.
	 * Updates the `userData` property upon success. Handles errors by hiding the spinner.
	 */
	/** Total users returned by the token-based endpoint (for display). */
	allUsersTotal = 0;

	loadMyUsers(event?: { first?: number; rows?: number }): void {
		const rows = event?.rows ?? this.myUsersRows;
		const offset = event?.first ?? (this.myUsersPage - 1) * rows;
		const search = (this.globalFilterValue || '').trim();

		this.myUsersRows = rows;
		this.myUsersPage = Math.floor(offset / rows) + 1;
		this.myUsersLoading = true;
		this.spinner.show();

		this.service.getALLUsersByBasedToken(rows, offset, search || undefined).subscribe({
			next: (res) => {
				if (Array.isArray(res?.data)) {
					this.allUsersData = this.normalizeUsersList(res.data);
					this.allUsersTotal = Number(res.total ?? res.data.length);
				} else if (Array.isArray(res)) {
					this.allUsersData = this.normalizeUsersList(res);
					this.allUsersTotal = res.length;
				} else {
					this.allUsersData = [];
					this.allUsersTotal = 0;
				}
				this.applyUserScope();
				this.myUsersLoading = false;
				this.spinner.hide();
			},
			error: (err) => {
				console.error('Error fetching users:', err);
				this.allUsersData = [];
				this.allUsersTotal = 0;
				this.applyUserScope();
				this.myUsersLoading = false;
				this.spinner.hide();
				this.messageService.add({ severity: 'error', summary: this.translate.instant('errors.fetchUsersFailed') });
			},
		});
	}

	getALLUsersByBasedToken() {
		this.loadMyUsers({ first: 0, rows: this.myUsersRows });
	}

	loadPlatformUsers(event?: { first?: number; rows?: number }): void {
		if (!this.canUsePlatformUsers) {
			this.userScope = 'all';
			this.applyUserScope();
			return;
		}

		const rows = event?.rows ?? this.platformRows;
		const offset = event?.first ?? (this.platformPage - 1) * rows;
		const search = (this.globalFilterValue || '').trim();

		this.platformRows = rows;
		this.platformPage = Math.floor(offset / rows) + 1;
		this.platformLoading = true;
		this.spinner.show();

		this.service.getAllPlatformUsers({
			offset,
			limit: rows,
			search: search || undefined,
		}).subscribe({
			next: (res) => {
				this.platformUsersData = this.normalizeUsersList(this.extractPlatformUsersRows(res));
				this.platformUsersTotal = Number(res?.total ?? this.platformUsersData.length);
				if (res?.limit) {
					this.platformRows = Number(res.limit);
				}
				if (res?.offset != null) {
					this.platformPage = Math.floor(Number(res.offset) / this.platformRows) + 1;
				}
				this.applyUserScope();
				this.platformLoading = false;
				this.spinner.hide();
			},
			error: (err) => {
				console.error("Error fetching platform users:", err);
				this.platformUsersData = [];
				this.platformUsersTotal = 0;
				this.applyUserScope();
				this.platformLoading = false;
				this.spinner.hide();
				this.messageService.add({ severity: 'error', summary: this.translate.instant('errors.fetchUsersFailed') });
			},
		});
	}

	onPlatformUsersLazyLoad(event: { first?: number; rows?: number }): void {
		if (!this.isPlatformUsersMode()) {
			return;
		}
		this.loadPlatformUsers(event);
	}

	onUsersTableLazyLoad(event: { first?: number; rows?: number }): void {
		if (this.isPlatformUsersMode()) {
			this.loadPlatformUsers(event);
			return;
		}
		this.loadMyUsers(event);
	}

	isUsersTableLoading(): boolean {
		return this.isPlatformUsersMode() ? this.platformLoading : this.myUsersLoading;
	}

	getUsersTableRows(): number {
		return this.isPlatformUsersMode() ? this.platformRows : this.myUsersRows;
	}

	getUsersTableTotal(): number {
		return this.isPlatformUsersMode() ? this.platformUsersTotal : this.allUsersTotal;
	}

	getUsersPageReport(): string {
		return this.isPlatformUsersMode() ? this.platformPageReport : this.myUsersPageReport;
	}

	onPlatformPageChange(page: number): void {
		this.platformPage = page;
		this.loadPlatformUsers({ first: (page - 1) * this.platformRows, rows: this.platformRows });
	}

	onUsersSearch(value: string): void {
		this.globalFilterValue = value;
		if (this.isPlatformUsersMode()) {
			this.platformSearchSubject.next(value);
			return;
		}
		this.myUsersSearchSubject.next(value);
	}

	onMyUsersPageChange(page: number): void {
		this.myUsersPage = page;
		this.loadMyUsers({ first: (page - 1) * this.myUsersRows, rows: this.myUsersRows });
	}

	onUserScopeChange(): void {
		if (this.userScope === 'admin' && !this.canUsePlatformUsers) {
			this.userScope = 'all';
			this.applyUserScope();
			return;
		}
		this.globalFilterValue = '';
		if (this.userScope === 'admin' && this.canUsePlatformUsers) {
			this.platformPage = 1;
			this.loadPlatformUsers({ first: 0, rows: this.platformRows });
			return;
		}
		this.myUsersPage = 1;
		this.loadMyUsers({ first: 0, rows: this.myUsersRows });
	}

	refreshUsersData(): void {
		if (this.userScope === 'admin' && this.canUsePlatformUsers) {
			this.loadPlatformUsers({ first: (this.platformPage - 1) * this.platformRows, rows: this.platformRows });
		} else {
			this.loadMyUsers({ first: (this.myUsersPage - 1) * this.myUsersRows, rows: this.myUsersRows });
		}
	}

	private extractPlatformUsersRows(res: any): any[] {
		if (Array.isArray(res)) {
			return res;
		}
		if (Array.isArray(res?.data)) {
			return res.data;
		}
		return [];
	}

	private normalizeUsersList(users: any[]): any[] {
		return (users || []).map((user) => this.normalizeUserRow(user));
	}

	private normalizeUserRow(user: any): any {
		if (!user || typeof user !== 'object') {
			return user;
		}

		const createdById = this.resolveCreatedById(user);
		let createdBy = user.createdBy
			?? user.created_by_user
			?? user.creator
			?? user.Creator
			?? user.createdByUser
			?? null;

		if (typeof createdBy === 'number') {
			createdBy = { user_id: createdBy };
		} else if (typeof createdBy === 'string' && createdBy.trim()) {
			createdBy = { user_name: createdBy.trim() };
		}

		const creatorName = this.extractCreatorNameFromSources(user, createdBy);
		const normalizedCreatedBy = creatorName
			? { ...(createdBy && typeof createdBy === 'object' ? createdBy : {}), user_name: creatorName }
			: (createdBy && typeof createdBy === 'object' ? createdBy : null);

		const normalized = {
			...user,
			created_by: createdById ?? user.created_by ?? null,
			createdBy: normalizedCreatedBy,
		};
		normalized.createdByDisplayName = this.resolveCreatedByDisplayName(normalized);
		return normalized;
	}

	private resolveCreatedById(user: any): number | null {
		const raw = user?.created_by
			?? user?.created_by_user_id
			?? user?.createdById
			?? user?.createdBy?.user_id
			?? null;
		if (raw == null || raw === '' || raw === 0) {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}

	private extractCreatorNameFromSources(user: any, createdBy: any): string | null {
		const candidates = [
			createdBy?.user_name,
			createdBy?.name,
			createdBy?.full_name,
			user?.created_by_user_name,
			user?.createdByUserName,
			user?.creator_user_name,
			user?.creator_name,
			user?.created_by_user?.user_name,
			user?.creator?.user_name,
		];

		for (const candidate of candidates) {
			if (typeof candidate === 'string' && candidate.trim()) {
				return candidate.trim();
			}
		}
		return null;
	}

	getCreatedByLabel(item: any): string {
		if (!item) {
			return this.translate.instant('users.table.createdByEmpty');
		}
		if (item.createdByDisplayName) {
			return item.createdByDisplayName;
		}
		return this.resolveCreatedByDisplayName(item);
	}

	isCreatedByFallback(item: any): boolean {
		return !this.extractCreatorNameFromSources(item, item?.createdBy);
	}

	private resolveCreatedByDisplayName(item: any): string {
		const name = this.extractCreatorNameFromSources(item, item?.createdBy);
		if (name) {
			return name;
		}
		if (!this.resolveCreatedById(item)) {
			return this.translate.instant('users.table.createdBySystem');
		}
		return this.translate.instant('users.table.createdByEmpty');
	}

	private buildCreatorPayload(): { created_by: number; created_by_user_id: number } | null {
		const creatorId = Number(this.userRegistId);
		if (!Number.isFinite(creatorId) || creatorId <= 0) {
			return null;
		}
		return {
			created_by: creatorId,
			created_by_user_id: creatorId,
		};
	}

	private applyUserScope(): void {
		if (this.userScope === 'admin') {
			// In platform mode, show all returned platform users (all types from admin endpoint).
			this.userData = this.platformUsersData || [];
			return;
		}
		this.userData = this.allUsersData || [];
	}

	isPlatformUsersMode(): boolean {
		return this.canUsePlatformUsers && this.userScope === 'admin';
	}

	getUserTypeLabel(item: any): string {
		const userType = +item?.user_type;
		const lookupType = item?.lookupDetailsUserType;

		if (lookupType) {
			if (this.curentLang === 'ar' && lookupType.lookupDetailArName) return lookupType.lookupDetailArName;
			if (this.curentLang === 'he' && lookupType.lookupDetailHeName) return lookupType.lookupDetailHeName;
			if (lookupType.lookupDetailEnName) return lookupType.lookupDetailEnName;
		}

		if (userType === 200) return this.translate.instant('users.types.admin');
		if (userType === 201) return this.translate.instant('users.types.adminAssistant');
		if (userType === 100) return this.translate.instant('users.types.merchant');
		if (userType === 101) return this.translate.instant('users.types.merchantUser');
		return String(userType || '-');
	}

	canShowPrivileges(item: any): boolean {
		if (this.isPlatformUsersMode()) return false;
		const userType = +item?.user_type;
		return userType === 101 || userType === 201;
	}

	canShowLockIcon(item: any): boolean {
		if (this.isPlatformUsersMode()) return false;
		const userType = +item?.user_type;
		return userType === 100 || userType === 200;
	}

	get canUseSecurityCode(): boolean {
		const t = +this.user_type;
		return (t === 200 || t === 201) && this.userScope === 'admin';
	}

	private promptAdminSecret(): Promise<string | null> {
		const labels = {
			ar: { title: 'كلمة سر الأدمن', placeholder: 'ادخل كلمة سر الأدمن', confirm: 'تأكيد', cancel: 'إلغاء' },
			he: { title: 'סיסמת מנהל', placeholder: 'הזן סיסמת מנהל', confirm: 'אישור', cancel: 'ביטול' },
			en: { title: 'Admin Secret', placeholder: 'Enter admin secret', confirm: 'Confirm', cancel: 'Cancel' },
		};
		const l = labels[this.curentLang] || labels.en;
		return Swal.fire({
			title: l.title,
			input: 'password',
			inputPlaceholder: l.placeholder,
			showCancelButton: true,
			confirmButtonText: l.confirm,
			cancelButtonText: l.cancel,
			confirmButtonColor: '#141414',
			inputValidator: (v) => !v ? l.placeholder : null,
		}).then(r => r.isConfirmed && r.value ? r.value : null);
	}

	async viewSecurityCode(userId: number) {
		const secret = await this.promptAdminSecret();
		if (!secret) return;
		this.spinner.show();
		this.service.getSecurityCode({ user_id: userId, admin_secret: secret }).subscribe({
			next: (res: any) => {
				this.spinner.hide();
				if (res.status === 403) {
					const msg = this.curentLang === 'ar' ? res.messageAr : this.curentLang === 'he' ? res.messageHe : res.messageEn;
					Swal.fire({ icon: 'error', title: msg || 'Forbidden' });
					return;
				}
				const audit = res.audit || {};
				const formatAudit = (a: any) => a?.at ? `${new Date(a.at).toLocaleString()} — ${a.by_user_name || ''}` : '—';
				const viewedLabel = this.curentLang === 'ar' ? 'آخر عرض' : this.curentLang === 'he' ? 'צפייה אחרונה' : 'Last viewed';
				const sentLabel = this.curentLang === 'ar' ? 'آخر إرسال' : this.curentLang === 'he' ? 'שליחה אחרונה' : 'Last sent';
				const changedLabel = this.curentLang === 'ar' ? 'آخر تغيير' : this.curentLang === 'he' ? 'שינוי אחרון' : 'Last changed';
				const copyLabel = this.curentLang === 'ar' ? 'نسخ' : this.curentLang === 'he' ? 'העתק' : 'Copy';
				const copiedLabel = this.curentLang === 'ar' ? 'تم النسخ!' : this.curentLang === 'he' ? 'הועתק!' : 'Copied!';
				const code = res.security_code || '—';
				Swal.fire({
					icon: 'info',
					title: this.curentLang === 'ar' ? 'كود الأمان' : this.curentLang === 'he' ? 'קוד אבטחה' : 'Security Code',
					html: `
						<div id="sc-copy-wrap" style="display:flex;align-items:center;justify-content:center;gap:10px;margin:16px 0;padding:12px 16px;background:#f8fafc;border:1px dashed #cbd5e1;border-radius:10px;cursor:pointer;" onclick="
							navigator.clipboard.writeText('${code}');
							var fb=document.getElementById('sc-copied-fb');
							var ic=document.getElementById('sc-copy-icon');
							if(fb){fb.style.display='inline';ic.className='fa fa-check';ic.style.color='#22c55e';}
							setTimeout(function(){if(fb){fb.style.display='none';ic.className='fa fa-clipboard';ic.style.color='#64748b';}},1500);
						">
							<span style="font-size:28px;font-weight:700;letter-spacing:6px;color:#141414;user-select:all;">${code}</span>
							<i id="sc-copy-icon" class="fa fa-clipboard" style="font-size:18px;color:#64748b;transition:color .2s;"></i>
						</div>
						<div style="text-align:center;margin-bottom:12px;">
							<span style="font-size:12px;color:#94a3b8;">${copyLabel}</span>
							<span id="sc-copied-fb" style="display:none;font-size:12px;color:#22c55e;font-weight:600;margin-inline-start:6px;">${copiedLabel}</span>
						</div>
						<hr style="margin:12px 0;">
						<div style="text-align:start;font-size:13px;color:#64748b;">
							<p><strong>${viewedLabel}:</strong> ${formatAudit(audit.last_viewed)}</p>
							<p><strong>${sentLabel}:</strong> ${formatAudit(audit.last_sent)}</p>
							<p><strong>${changedLabel}:</strong> ${formatAudit(audit.last_changed)}</p>
						</div>
					`,
					confirmButtonColor: '#141414',
				});
			},
			error: (err) => {
				this.spinner.hide();
				const body = err?.error || {};
				const msg = this.curentLang === 'ar' ? body.messageAr : this.curentLang === 'he' ? body.messageHe : (body.messageEn || body.message);
				Swal.fire({ icon: 'error', title: msg || 'Error' });
			},
		});
	}

	async sendSecurityCodeSms(userId: number) {
		const secret = await this.promptAdminSecret();
		if (!secret) return;
		this.spinner.show();
		this.service.sendSecurityCode({ user_id: userId, admin_secret: secret }).subscribe({
			next: (res: any) => {
				this.spinner.hide();
				if (res.status === 403) {
					const msg = this.curentLang === 'ar' ? res.messageAr : this.curentLang === 'he' ? res.messageHe : res.messageEn;
					Swal.fire({ icon: 'error', title: msg || 'Forbidden' });
					return;
				}
				const msg = this.curentLang === 'ar' ? res.messageAr : this.curentLang === 'he' ? res.messageHe : res.messageEn;
				Swal.fire({ icon: 'success', title: msg || 'Sent', confirmButtonColor: '#141414' });
			},
			error: (err) => {
				this.spinner.hide();
				const body = err?.error || {};
				const msg = this.curentLang === 'ar' ? body.messageAr : this.curentLang === 'he' ? body.messageHe : (body.messageEn || body.message);
				Swal.fire({ icon: 'error', title: msg || 'Error' });
			},
		});
	}

	async rotateSecurityCode(userId: number) {
		const confirmLabel = this.curentLang === 'ar' ? 'هل أنت متأكد من تغيير كود الأمان؟' : this.curentLang === 'he' ? 'האם אתה בטוח שברצונך לשנות את קוד האבטחה?' : 'Are you sure you want to change the security code?';
		const preConfirm = await Swal.fire({
			icon: 'warning',
			title: confirmLabel,
			showCancelButton: true,
			confirmButtonText: this.curentLang === 'ar' ? 'نعم' : this.curentLang === 'he' ? 'כן' : 'Yes',
			cancelButtonText: this.curentLang === 'ar' ? 'إلغاء' : this.curentLang === 'he' ? 'ביטול' : 'Cancel',
			confirmButtonColor: '#dc2626',
		});
		if (!preConfirm.isConfirmed) return;

		const secret = await this.promptAdminSecret();
		if (!secret) return;
		this.spinner.show();
		this.service.changeSecurityCode({ user_id: userId, admin_secret: secret }).subscribe({
			next: (res: any) => {
				this.spinner.hide();
				if (res.status === 403) {
					const msg = this.curentLang === 'ar' ? res.messageAr : this.curentLang === 'he' ? res.messageHe : res.messageEn;
					Swal.fire({ icon: 'error', title: msg || 'Forbidden' });
					return;
				}
				const msg = this.curentLang === 'ar' ? res.messageAr : this.curentLang === 'he' ? res.messageHe : res.messageEn;
				const newCode = res.security_code || '';
				const cpLabel = this.curentLang === 'ar' ? 'نسخ' : this.curentLang === 'he' ? 'העתק' : 'Copy';
				const cpdLabel = this.curentLang === 'ar' ? 'تم النسخ!' : this.curentLang === 'he' ? 'הועתק!' : 'Copied!';
				Swal.fire({
					icon: 'success',
					title: msg || 'Changed',
					html: `
						<div id="sc2-copy-wrap" style="display:flex;align-items:center;justify-content:center;gap:10px;margin:16px 0;padding:12px 16px;background:#f0fdf4;border:1px dashed #86efac;border-radius:10px;cursor:pointer;" onclick="
							navigator.clipboard.writeText('${newCode}');
							var fb=document.getElementById('sc2-copied-fb');
							var ic=document.getElementById('sc2-copy-icon');
							if(fb){fb.style.display='inline';ic.className='fa fa-check';ic.style.color='#22c55e';}
							setTimeout(function(){if(fb){fb.style.display='none';ic.className='fa fa-clipboard';ic.style.color='#64748b';}},1500);
						">
							<span style="font-size:28px;font-weight:700;letter-spacing:6px;color:#141414;user-select:all;">${newCode}</span>
							<i id="sc2-copy-icon" class="fa fa-clipboard" style="font-size:18px;color:#64748b;transition:color .2s;"></i>
						</div>
						<div style="text-align:center;">
							<span style="font-size:12px;color:#94a3b8;">${cpLabel}</span>
							<span id="sc2-copied-fb" style="display:none;font-size:12px;color:#22c55e;font-weight:600;margin-inline-start:6px;">${cpdLabel}</span>
						</div>
					`,
					confirmButtonColor: '#141414',
				});
			},
			error: (err) => {
				this.spinner.hide();
				const body = err?.error || {};
				const msg = this.curentLang === 'ar' ? body.messageAr : this.curentLang === 'he' ? body.messageHe : (body.messageEn || body.message);
				Swal.fire({ icon: 'error', title: msg || 'Error' });
			},
		});
	}

	/**
	 * Fetches all available user types from the backend using the `HttpHelpService`.
	 * Displays a spinner during the request. Updates the `userTypeData` property upon success.
	 * Handles errors by hiding the spinner.
	 */
	getAllUserType() {
		this.spinner.show();
		this.service.getAllUserType().subscribe({
			next: (res) => {
				this.userTypeData = res;
				this.spinner.hide();
			},
			error: (err) => {
				console.error("Error fetching user types:", err);
				this.spinner.hide();

				this.messageService.add({ severity: 'error', summary: this.translate.instant('errors.fetchUserTypesFailed') });
			}
		});
	}

	/**
	 * Opens a modal dialog using the `NgbModal` service with an extra-large size ('xl')
	 * and centers it on the screen.
	 * @param content - The template reference (`<ng-template>`) or component type to display within the modal.
	 */
	openXl(content: any) {
		this.modalService.open(content, { size: 'xl', centered: true });
	}

	/**
	 * Opens a modal dialog using the `NgbModal` service with a medium size ('md')
	 * and centers it on the screen.
	 * @param content2 - The template reference (`<ng-template>`) or component type to display within the modal.
	 */
	openXl2(content2: any) {
		this.modalService.open(content2, { size: 'md', centered: true });
	}

	/**
	 * Marks the 'password' FormControl within the provided FormGroup as 'touched'.
	 * This is typically used to trigger validation messages when the user interacts
	 * with related elements (like a show/hide password button) without directly blurring the input.
	 * @param formGroup - The FormGroup containing the 'password' control.
	 */
	handlePasswordValidation(formGroup: FormGroup) {
		const passwordControl = formGroup.get('password');
		if (passwordControl) {
			passwordControl.markAsTouched();
		}
	}

	/**
	 * Toggles the `show` property to control password visibility in the template.
	 * Attempts to focus the password input element after toggling.
	 * Also triggers password validation marking for both add user forms.
	 */
	showPassword() {
		this.show = !this.show;

		const passwordInput = document.querySelector('input[name="login[password]"]') as HTMLInputElement;
		if (passwordInput) {

			passwordInput.focus();
		}

		this.handlePasswordValidation(this.addUser);
		this.handlePasswordValidation(this.merchantUser);
	}

	/**
	 * Handles the submission of the 'add standard user' form (`addUser`).
	 * Performs client-side validation (required fields, phone format, password length).
	 * If valid, formats the phone number, constructs the request payload,
	 * and sends a POST request to add the user via `HttpHelpService`.
	 * Shows success or error messages and refreshes the user list on success.
	 */
	addedData() {

		this.addUser.markAllAsTouched();
		this.validate = true;

		if (this.addUser.invalid) {
			if (this.addUser.get('user_name')?.hasError('maxlength')) {
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('users.addUser.userNameMaxLength'),
				});
			} else {
				this.messageService.add({
					severity: 'warn',
					summary: this.translate.instant('users.addUser.fillAllFields'),
				});
			}
			return;
		}


		let userPhone = this.addUser.value.phone_number || '';
		if (userPhone.startsWith('0')) {
			userPhone = userPhone.substring(1);
		}

		const validPrefixes = ['50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
		let validationStartPhoneNumber = userPhone.substring(0, 2);

		if (!validPrefixes.includes(validationStartPhoneNumber) || userPhone.length < 9) {
			this.messageService.add({
				severity: 'warn',
				summary: this.translate.instant('users.addUser.invalidPhoneFormat')
			});
			return;
		}


		let password = this.addUser.value.password || '';
		if (password.length < 8 || password.length > 20) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('users.addUser.passwordLengthError')
			});
			return;
		}


		const creatorPayload = this.buildCreatorPayload();
		if (!creatorPayload) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('users.addUser.unknownError')
			});
			return;
		}

		let model = {
			"phone_number": '+972' + userPhone,
			user_name: this.addUser.value.user_name,
			password: this.addUser.value.password,
			user_type: this.addUser.value.user_type,
			language: this.curentLang,
			...creatorPayload,
		};

		this.spinner.show();

		this.service.Post(AddUserEndPoient.POST, model).subscribe({
			next: (res: any) => {
				if (res.status === 200) {
					this.messageService.add({
						severity: 'success',
						summary: this.translate.instant(res.messageKey || 'users.addUser.success')
					});
					this.modalService.dismissAll();
					this.refreshUsersData();

					this.addUser.reset({ language: this.curentLang, created_by: this.userRegistId });
					this.validate = false;
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.resolveApiMessage(res, 'users.addUser.unknownError'),
						life: 6000,
					});
				}
				this.spinner.hide();
			},
			error: (err: any) => {
				console.error("Error adding user:", err);
				this.messageService.add({
					severity: 'error',
					summary: this.resolveApiErrorSummary(err, 'users.addUser.unknownError'),
					life: 6000,
				});
				this.spinner.hide();
			}
		});
	}

	/**
	 * Handles the submission of the 'add merchant user' form (`merchantUser`).
	 * Performs client-side validation (required fields, phone format, password length).
	 * If valid, formats the phone number, constructs the request payload,
	 * and sends a POST request to add the merchant user via `HttpHelpService`.
	 * Shows success or error messages and refreshes the user list on success.
	 */
	addMerchantUser() {

		this.merchantUser.markAllAsTouched();
		this.validate = true;

		if (this.merchantUser.invalid) {
			if (this.merchantUser.get('user_name')?.hasError('maxlength')) {
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('users.addUser.userNameMaxLength'),
				});
			} else {
				this.messageService.add({
					severity: 'warn',
					summary: this.translate.instant('users.addUser.fillAllFields'),
				});
			}
			return;
		}


		let userPhone = this.merchantUser.value.phone_number || '';
		if (userPhone.startsWith('0')) {
			userPhone = userPhone.substring(1);
		}

		const validPrefixes = ['50', '51', '52', '53', '54', '55', '56', '57', '58', '59'];
		let validationStartPhoneNumber = userPhone.substring(0, 2);

		if (!validPrefixes.includes(validationStartPhoneNumber) || userPhone.length < 9) {
			this.messageService.add({
				severity: 'warn',
				summary: this.translate.instant('users.addUser.invalidPhoneFormat')
			});
			return;
		}

		let password = this.merchantUser.value.password || '';
		if (password.length < 8 || password.length > 20) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('users.addUser.passwordLengthError')
			});
			return;
		}


		const creatorPayload = this.buildCreatorPayload();
		if (!creatorPayload) {
			this.messageService.add({
				severity: 'error',
				summary: this.translate.instant('users.addUser.unknownError')
			});
			return;
		}

		let model = {
			"phone_number": '+972' + userPhone,
			user_name: this.merchantUser.value.user_name,
			password: this.merchantUser.value.password,
			merchant_id: Number(this.merchantId),
			...creatorPayload,
		};

		this.spinner.show();

		this.service.Post(AddMerchantUserEndPoient.POST, model).subscribe({
			next: (res: any) => {
				if (res.status === 200) {
					this.messageService.add({
						severity: 'success',
						summary: this.translate.instant(res.messageKey || 'users.addMerchantUser.success')
					});
					this.modalService.dismissAll();
					this.refreshUsersData();

					this.merchantUser.reset({ merchant_id: this.merchantId, created_by: this.userRegistId });
					this.validate = false;
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.resolveApiMessage(res, 'users.addUser.unknownError'),
						life: 6000,
					});
				}
				this.spinner.hide();
			},
			error: (err: any) => {
				console.error("Error adding merchant user:", err);
				this.messageService.add({
					severity: 'error',
					summary: this.resolveApiErrorSummary(err, 'users.addUser.unknownError'),
					life: 6000,
				});
				this.spinner.hide();
			}
		});
	}

	/**
	 * Changes the status of a user (e.g., active to inactive or vice-versa).
	 * It determines the new status based on the current status (toggles between 1 and 59).
	 * Sends a request to the backend API to update the user's status.
	 * Shows success or error messages and refreshes the user list on success.
	 * @param id - The ID of the user whose status needs to be changed.
	 * @param user_status - The current status of the user (expected to be 1 or 59).
	 */
	changeUserStatus(id: number, user_status: any) {
		this.spinner.show();


		const newStatus = user_status == 1 ? 59 : 1;

		let model = {
			"user_status": newStatus,
			"updated_by": this.userRegistId
		};

		this.service.changeUserStatus(id, model).subscribe({
			next: (res: any) => {
				if (res.status == 200) {
					this.messageService.add({
						severity: 'success',
						summary: this.translate.instant(res.messageKey || 'users.changeStatus.success')
					});
					this.refreshUsersData();
				} else {

					this.messageService.add({
						severity: 'warn',
						summary: this.resolveApiMessage(res, 'users.changeStatus.warning'),
						life: 6000,
					});
				}
				this.spinner.hide();
			},
			error: (err: any) => {
				console.error("Error changing user status:", err);
				this.messageService.add({
					severity: 'error',
					summary: this.resolveApiErrorSummary(err, 'users.changeStatus.error'),
					life: 6000,
				});
				this.spinner.hide();
			}
		});
	}

	/**
	 * Fetches an auto-login URL for a specific user from the backend.
	 * If successful, copies the URL to the clipboard using the `navigator.clipboard` API.
	 * Shows appropriate success or error messages to the user.
	 * @param userId - The ID of the user for whom to generate and copy the login URL.
	 */
	copyLoginUrl(userId: number) {
		this.spinner.show();
		this.service.getAutoLoginUrl(userId).subscribe({
			next: (res: any) => {
				if (res.status === 200 && res.url) {
					navigator.clipboard.writeText(res.url).then(() => {
						this.messageService.add({
							severity: 'success',
							summary: this.translate.instant('users.copyLoginUrl.success')
						});
						this.spinner.hide();
					}).catch(err => {
						console.error('Failed to copy URL: ', err);
						this.messageService.add({
							severity: 'error',
							summary: this.translate.instant('users.copyLoginUrl.clipboardError')
						});
						this.spinner.hide();
					});
				} else {

					console.error("Failed to get login URL from response:", res);
					this.messageService.add({
						severity: 'error',
						summary: this.resolveApiMessage(res, 'users.copyLoginUrl.error'),
						life: 6000,
					});
					this.spinner.hide();
				}
			},
			error: (err: any) => {
				console.error("Error fetching auto-login URL:", err);
				this.messageService.add({
					severity: 'error',
					summary: this.resolveApiErrorSummary(err, 'users.copyLoginUrl.error'),
					life: 6000,
				});
				this.spinner.hide();
			}
		});
	}

	/**
	 * Angular lifecycle hook called after component initialization.
	 * Fetches the initial list of users and user types.
	 * Retrieves layout settings, user ID, merchant ID, and user type from local storage.
	 * Applies the layout theme ('dark-only' or 'light-only') to the document body.
	 */
	ngOnInit() {
		this.platformSearchSubject.pipe(
			debounceTime(400),
			distinctUntilChanged(),
			takeUntil(this.destroy$),
		).subscribe(() => {
			if (!this.isPlatformUsersMode()) {
				return;
			}
			this.platformPage = 1;
			this.loadPlatformUsers({ first: 0, rows: this.platformRows });
		});

		this.myUsersSearchSubject.pipe(
			debounceTime(400),
			distinctUntilChanged(),
			takeUntil(this.destroy$),
		).subscribe(() => {
			if (this.isPlatformUsersMode()) {
				return;
			}
			this.myUsersPage = 1;
			this.loadMyUsers({ first: 0, rows: this.myUsersRows });
		});

		this.refreshUsersData();
		this.getAllUserType();


		this.layoutVersion = localStorage.getItem("layout_version");
		if (!this.layoutVersion) {
			this.layoutVersion = "light-only";
			localStorage.setItem("layout_version", this.layoutVersion);
		}


		document.body.classList.remove('dark-only', 'light-only');
		if (this.layoutVersion === "dark-only") {
			document.body.classList.add("dark-only");
		} else {
			document.body.classList.add("light-only");
		}



		this.userRegistId = JSON.parse(localStorage.getItem('user_id') || 'null');
		this.merchantId = JSON.parse(localStorage.getItem('merchant_id') || 'null');
		this.user_type = JSON.parse(localStorage.getItem('user_type') || 'null');


		this.addUser.patchValue({ created_by: this.userRegistId });
		this.merchantUser.patchValue({ merchant_id: this.merchantId, created_by: this.userRegistId });
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Opens a modal dialog using the `NgbModal` service with a large size ('lg').
	 * @param content4 - The template reference (`<ng-template>`) or component type to display within the modal.
	 */
	openXl4(content4: any) {
		this.modalService.open(content4, { size: 'lg' });
	}

	/**
	 * Sets the `userId` property with the provided ID.
	 * This is typically called when a user selects a user from the list for an action (e.g., delete, edit status).
	 * @param id - The ID of the user to be stored.
	 */
	getId(id: number) {
		this.userId = id;
	}

	/**
	 * Clears all filters applied to the PrimeNG table instance.
	 * @param table - The PrimeNG Table component instance (e.g., `<p-table #dt>`).
	 */
	clear(table: any) {
		if (table && typeof table.clear === 'function') {
			table.clear();
		}

		this.globalFilterValue = '';
	}

	/**
	 * Clears the global filter input field and resets the PrimeNG table's global filter.
	 * @param table - The PrimeNG Table component instance.
	 * @param input - The HTMLInputElement used for the global filter.
	 */
	clearGlobalFilter(table: any, input: HTMLInputElement) {
		this.globalFilterValue = '';
		if (input) {
			input.value = '';
		}
		if (this.isPlatformUsersMode()) {
			this.platformPage = 1;
			this.loadPlatformUsers({ first: 0, rows: this.platformRows });
			return;
		}
		this.myUsersPage = 1;
		this.loadMyUsers({ first: 0, rows: this.myUsersRows });
	}

	/** API message for toast — supports messageAr/messageEn/messageHe and Nest validation errors. */
	private resolveApiMessage(body: any, fallbackKey: string): string {
		if (!body || typeof body !== 'object') {
			return this.translate.instant(fallbackKey);
		}

		if (body.messageKey) {
			const translated = this.translate.instant(body.messageKey);
			if (translated !== body.messageKey) {
				return translated;
			}
		}

		const lang = (this.curentLang || 'en').toLowerCase();
		const byLang =
			lang === 'ar'
				? body.messageAr ?? body.message_ar
				: lang === 'he'
					? body.messageHe ?? body.message_he
					: body.messageEn ?? body.message_en;
		if (typeof byLang === 'string' && byLang.trim()) {
			return byLang.trim();
		}

		const anyLocalized = [
			body.messageAr,
			body.message_ar,
			body.messageEn,
			body.message_en,
			body.messageHe,
			body.message_he,
		].find((value) => typeof value === 'string' && value.trim());
		if (anyLocalized) {
			return String(anyLocalized).trim();
		}

		const rawMessage = body.message;
		if (Array.isArray(rawMessage)) {
			return rawMessage
				.map((entry) => this.mapValidationMessage(String(entry)))
				.filter(Boolean)
				.join(' · ');
		}
		if (typeof rawMessage === 'string' && rawMessage.trim()) {
			return this.mapValidationMessage(rawMessage.trim());
		}

		if (typeof body.error === 'string' && body.error !== 'Bad Request') {
			return body.error;
		}

		return this.translate.instant(fallbackKey);
	}

	/** HttpClient error wrapper → same message resolver as 200 responses with status !== 200. */
	private resolveApiErrorSummary(err: any, fallbackKey: string): string {
		return this.resolveApiMessage(err?.error ?? err, fallbackKey);
	}

	private mapValidationMessage(message: string): string {
		const normalized = message.toLowerCase();
		if (normalized.includes('user_name') && normalized.includes('20')) {
			return this.translate.instant('users.addUser.userNameMaxLength');
		}
		if (normalized.includes('password') && (normalized.includes('longer') || normalized.includes('8'))) {
			return this.translate.instant('users.addUser.passwordMinLength');
		}
		return message;
	}
}
